// src/pages/account/InventoryDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Alert,
  Snackbar,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
  useTheme,
  alpha,
  Tab,
  Tabs,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  History as HistoryIcon,
  ArrowUpward as InIcon,
  ArrowDownward as OutIcon,
  Store as StoreIcon,
  Link as LinkIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  RestaurantMenu as MenuIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  ShowChart as ShowChartIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';

// Import Recharts for charts
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Bar as RechartsBar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

// Import theme colors and preloader
import { COLORS } from '../../../theme/colors';
import Preloader from '../../../theme/Preloader';

// Lazy load components (Account versions)
const AccountSidebar = lazy(() => import('../../../components/Account/Sidebar'));
const AccountHeader = lazy(() => import('../../../components/Account/Header'));
const AccountFooter = lazy(() => import('../../../components/Account/Footer'));

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// ============================================
// TOKEN UTILITY FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    return true;
  }
};

const getAuthToken = () => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token || isTokenExpired(token)) {
      return null;
    }
    return token;
  } catch (err) {
    return null;
  }
};

// ============================================
// UTILITY FUNCTIONS & CONSTANTS
// ============================================

const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: COLORS.primary?.african || '#2E7D32',
    lightColor: '#4CAF50',
    icon: '🌍',
    path: '/account/african-inventory',
    label: 'African Inventory'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: COLORS.primary?.continental || '#1565C0',
    lightColor: '#1976D2',
    icon: '🌐',
    path: '/account/continental-inventory',
    label: 'Continental Inventory'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: COLORS.primary?.lounge || '#6A1B9A',
    lightColor: '#9C27B0',
    icon: '🍸',
    path: '/account/lounge-inventory',
    label: 'Lounge Inventory'
  }
};

const STATUS_COLORS = {
  IN_STOCK: '#4caf50',
  LOW_STOCK: '#ff9800',
  OUT_OF_STOCK: '#f44336',
  DISCONTINUED: '#9e9e9e',
};

const STATUS_LABELS = {
  IN_STOCK: 'In Stock',
  LOW_STOCK: 'Low Stock',
  OUT_OF_STOCK: 'Out of Stock',
  DISCONTINUED: 'Discontinued',
};

const MOVEMENT_TYPE_LABELS = {
  PURCHASE: 'Purchase',
  SALE: 'Sale',
  ADJUSTMENT: 'Adjustment',
  RETURN: 'Return',
  WASTAGE: 'Wastage',
  TRANSFER: 'Transfer',
};

const MOVEMENT_TYPE_COLORS = {
  PURCHASE: '#4caf50',
  SALE: '#f44336',
  ADJUSTMENT: '#ff9800',
  RETURN: '#2196f3',
  WASTAGE: '#9e9e9e',
  TRANSFER: '#9c27b0',
};

const CHART_COLORS = ['#4caf50', '#ff9800', '#f44336', '#2196f3', '#9c27b0', '#00bcd4', '#ff6f00', '#00695c'];

// ============================================
// SUB-COMPONENTS
// ============================================

// Stat Card Component (View Only)
const StatCard = React.memo(({ title, value, icon, color, subtitle }) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
            {title}
          </Typography>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 32, height: 32 }}>
            {icon}
          </Avatar>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: color, mt: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

// Inventory Item Row with View Stock button
const InventoryItemRow = React.memo(({ 
  item, 
  onViewStock,
  onViewDetails,
  index 
}) => {
  const theme = useTheme();
  const statusColor = STATUS_COLORS[item.status] || '#9e9e9e';
  const statusLabel = STATUS_LABELS[item.status] || item.status;

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600}>
            {item.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {item.category || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {item.quantity} {item.unit}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            backgroundColor: alpha(statusColor, 0.1),
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          ₦{item.cost_per_unit?.toFixed(2) || '0.00'}
        </Typography>
      </TableCell>
      <TableCell>
        {item.menu_item_name ? (
          <Chip
            label={item.menu_item_name}
            size="small"
            icon={<LinkIcon sx={{ fontSize: 14 }} />}
            sx={{ fontWeight: 500 }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">Not linked</Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => onViewDetails(item)}
              sx={{ color: theme.palette.info.main }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Stock">
            <IconButton
              size="small"
              onClick={() => onViewStock(item)}
              sx={{ color: theme.palette.info.main }}
            >
              <StoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

// Movement History Row Component
const MovementRow = React.memo(({ movement }) => {
  const isIn = movement.movement_type === 'PURCHASE' || movement.movement_type === 'RETURN';
  const typeColor = MOVEMENT_TYPE_COLORS[movement.movement_type] || '#9e9e9e';
  const typeLabel = MOVEMENT_TYPE_LABELS[movement.movement_type] || movement.movement_type;

  return (
    <TableRow>
      <TableCell>{new Date(movement.created_at).toLocaleString()}</TableCell>
      <TableCell>
        <Chip
          label={typeLabel}
          size="small"
          sx={{
            backgroundColor: alpha(typeColor, 0.1),
            color: typeColor,
            fontWeight: 600,
          }}
          icon={isIn ? <InIcon sx={{ fontSize: 14 }} /> : <OutIcon sx={{ fontSize: 14 }} />}
        />
      </TableCell>
      <TableCell sx={{ fontWeight: 600, color: isIn ? '#4caf50' : '#f44336' }}>
        {isIn ? '+' : '-'}{movement.quantity} {movement.unit}
      </TableCell>
      <TableCell>{movement.previous_quantity} → {movement.new_quantity}</TableCell>
      <TableCell>{movement.reason || '-'}</TableCell>
      <TableCell>{movement.performed_by_name || '-'}</TableCell>
    </TableRow>
  );
});

// Alert Item Component
const AlertItem = React.memo(({ alert }) => {
  const isLowStock = alert.alert_type === 'LOW_STOCK';
  
  return (
    <ListItem
      divider
      sx={{
        backgroundColor: alpha(
          isLowStock ? '#ff9800' : '#f44336',
          0.05
        ),
        borderRadius: 1,
        mb: 1,
        '&:last-child': { mb: 0 },
      }}
    >
      <ListItemIcon>
        {isLowStock ? (
          <WarningIcon color="warning" />
        ) : (
          <CancelIcon color="error" />
        )}
      </ListItemIcon>
      <ListItemText
        primary={alert.message}
        secondary={
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="textSecondary">
              Current: {alert.current_quantity} {alert.unit || ''}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Threshold: {alert.threshold} {alert.unit || ''}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Created: {new Date(alert.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        }
      />
    </ListItem>
  );
});

// ============================================
// MAIN COMPONENT
// ============================================

const InventoryDashboard = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================

  // Inventory items state
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredInventoryItems, setFilteredInventoryItems] = useState([]);
  
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({
    total_items: 0,
    active_items: 0,
    inactive_items: 0,
    low_stock_count: 0,
    out_of_stock_count: 0,
    total_quantity: 0,
    total_value: 0,
    categories: {},
    menu_item_count: 0,
    menu_items_with_inventory: 0,
  });
  
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Inventory Dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tabValue, setTabValue] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Filter state for inventory items
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  // View Stock dialog state - EXACT PATTERN FROM EXAMPLE
  const [viewStockDialogOpen, setViewStockDialogOpen] = useState(false);
  const [viewStockItem, setViewStockItem] = useState(null);
  const [viewStockData, setViewStockData] = useState(null);
  const [viewStockMovements, setViewStockMovements] = useState([]);
  const [loadingStockData, setLoadingStockData] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Chart data
  const [trendData, setTrendData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [valueData, setValueData] = useState([]);
  const [movementTypeData, setMovementTypeData] = useState([]);
  const [stockHealthData, setStockHealthData] = useState([]);

  // Get location config
  const locationConfig = useMemo(() => 
    LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african,
    [locationKey]
  );
  
  const { name: locationName, id: locationIdValue, color: locationColor } = locationConfig;

  // ============================================
  // API FUNCTIONS
  // ============================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    const url = `${FULL_API_URL}${endpoint}`;
    
    try {
      const response = await fetch(url, { ...options, headers });
      
      if (response.status === 401) {
        localStorage.clear();
        window.location.href = '/account_LoginPage';
        throw new Error('Session expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }
      
      return data;
    } catch (err) {
      console.error('[Account InventoryDashboard] API Error:', err);
      throw err;
    }
  }, []);

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchInventoryItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/inventory/location/${locationName}/items?limit=1000`);
      const itemsData = Array.isArray(data) ? data : [];
      setInventoryItems(itemsData);
      setFilteredInventoryItems(itemsData);
      
      // Calculate stats from items
      const total = itemsData.length;
      const active = itemsData.filter(i => i.is_active !== false).length;
      const lowStock = itemsData.filter(i => i.status === 'LOW_STOCK').length;
      const outOfStock = itemsData.filter(i => i.status === 'OUT_OF_STOCK').length;
      const totalQuantity = itemsData.reduce((sum, i) => sum + (i.quantity || 0), 0);
      const totalValue = itemsData.reduce((sum, i) => sum + ((i.quantity || 0) * (i.cost_per_unit || 0)), 0);
      
      // Category breakdown
      const categories = {};
      const categoryValues = {};
      itemsData.forEach(item => {
        const cat = item.category || 'Uncategorized';
        categories[cat] = (categories[cat] || 0) + 1;
        categoryValues[cat] = (categoryValues[cat] || 0) + ((item.quantity || 0) * (item.cost_per_unit || 0));
      });
      
      // Prepare value by category data
      const valueChartData = Object.entries(categoryValues).map(([name, value]) => ({
        name,
        value: Math.round(value),
      })).sort((a, b) => b.value - a.value);
      setValueData(valueChartData);

      // Prepare stock health data
      const healthData = [
        { name: 'In Stock', value: Math.max(0, total - lowStock - outOfStock) },
        { name: 'Low Stock', value: lowStock },
        { name: 'Out of Stock', value: outOfStock },
      ];
      setStockHealthData(healthData);
      
      setStats(prev => ({
        ...prev,
        total_items: total,
        active_items: active,
        inactive_items: total - active,
        low_stock_count: lowStock,
        out_of_stock_count: outOfStock,
        total_quantity: totalQuantity,
        total_value: totalValue,
        categories,
      }));

      // Prepare category chart data
      const categoryChartData = Object.entries(categories).map(([name, value]) => ({
        name,
        value,
      })).sort((a, b) => b.value - a.value);
      setCategoryData(categoryChartData);

      return itemsData;
    } catch (err) {
      console.error('[Account InventoryDashboard] Error fetching inventory:', err);
      setSnackbar({
        open: true,
        message: `❌ Failed to fetch inventory: ${err.message}`,
        severity: 'error',
      });
      return [];
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, [apiRequest, locationName]);

  const fetchMovements = useCallback(async () => {
    try {
      const data = await apiRequest(`/inventory/movements?location_id=${locationIdValue}&limit=200`);
      const movementsData = Array.isArray(data) ? data : [];
      setMovements(movementsData);
      
      // Prepare trend data from movements
      const trendMap = {};
      const typeMap = {};
      
      movementsData.forEach(m => {
        const date = new Date(m.created_at).toLocaleDateString();
        if (!trendMap[date]) {
          trendMap[date] = { date, purchases: 0, sales: 0, adjustments: 0, returns: 0, wastage: 0 };
        }
        if (m.movement_type === 'PURCHASE') {
          trendMap[date].purchases += m.quantity;
        } else if (m.movement_type === 'SALE') {
          trendMap[date].sales += m.quantity;
        } else if (m.movement_type === 'RETURN') {
          trendMap[date].returns += m.quantity;
        } else if (m.movement_type === 'WASTAGE') {
          trendMap[date].wastage += m.quantity;
        } else {
          trendMap[date].adjustments += Math.abs(m.quantity);
        }
        
        // Count movement types
        typeMap[m.movement_type] = (typeMap[m.movement_type] || 0) + 1;
      });
      
      const trendArray = Object.values(trendMap).slice(-14);
      setTrendData(trendArray);

      // Prepare movement type data
      const typeChartData = Object.entries(typeMap).map(([type, count]) => ({
        name: MOVEMENT_TYPE_LABELS[type] || type,
        value: count,
        color: MOVEMENT_TYPE_COLORS[type] || '#9e9e9e',
      }));
      setMovementTypeData(typeChartData);
      
    } catch (err) {
      console.error('[Account InventoryDashboard] Error fetching movements:', err);
    }
  }, [apiRequest, locationIdValue]);

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await apiRequest(`/inventory/alerts?location_id=${locationIdValue}&resolved=false`);
      setAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[Account InventoryDashboard] Error fetching alerts:', err);
    }
  }, [apiRequest, locationIdValue]);

  // ============================================
  // FIXED: FETCH MOVEMENTS FOR ITEM - EXACT PATTERN FROM EXAMPLE
  // ============================================

  const fetchMovementsForItem = useCallback(async (itemId) => {
    setLoadingStockData(true);
    try {
      const data = await apiRequest(`/inventory/movements?inventory_item_id=${itemId}&limit=200`);
      const movementsData = Array.isArray(data) ? data : [];
      setViewStockMovements(movementsData);
      return movementsData;
    } catch (err) {
      console.error('[Account InventoryDashboard] Error fetching movements:', err);
      setSnackbar({
        open: true,
        message: `Failed to fetch movements: ${err.message}`,
        severity: 'error',
      });
      return [];
    } finally {
      setLoadingStockData(false);
    }
  }, [apiRequest]);

  // ============================================
  // FIXED: VIEW STOCK HANDLER - EXACT PATTERN FROM EXAMPLE
  // ============================================

  const handleViewStock = useCallback(async (item) => {
    // Reset all states
    setViewStockItem(item);
    setViewStockDialogOpen(true);
    setLoadingStockData(true);
    setViewStockData(null);
    setViewStockMovements([]);
    
    try {
      // Set inventory data
      setViewStockData(item);
      
      // Fetch movements for this item
      await fetchMovementsForItem(item.id);
      
    } catch (err) {
      console.error('[Account InventoryDashboard] Error fetching stock data:', err);
      setSnackbar({
        open: true,
        message: `Failed to fetch stock data: ${err.message}`,
        severity: 'error',
      });
      setViewStockMovements([]);
    } finally {
      setLoadingStockData(false);
    }
  }, [fetchMovementsForItem]);

  const handleCloseViewStockDialog = useCallback(() => {
    setViewStockDialogOpen(false);
    setViewStockItem(null);
    setViewStockData(null);
    setViewStockMovements([]);
    setLoadingStockData(false);
  }, []);

  const fetchAllData = useCallback(async () => {
    await Promise.all([
      fetchInventoryItems(),
      fetchMovements(),
      fetchAlerts(),
    ]);
  }, [fetchInventoryItems, fetchMovements, fetchAlerts]);

  // ============================================
  // DIALOG HANDLERS
  // ============================================

  const handleViewItem = useCallback((item) => {
    setViewItem(item);
    setViewDialogOpen(true);
  }, []);

  const handleCloseViewDialog = useCallback(() => {
    setViewDialogOpen(false);
    setViewItem(null);
  }, []);

  // ============================================
  // SEARCH AND FILTER
  // ============================================

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Apply filters to inventory items
  const applyInventoryFilters = useCallback(() => {
    let filtered = [...inventoryItems];
    
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.category?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.supplier?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }
    
    setFilteredInventoryItems(filtered);
    setPage(0);
  }, [inventoryItems, debouncedSearchTerm, statusFilter, categoryFilter]);

  useEffect(() => {
    applyInventoryFilters();
  }, [applyInventoryFilters]);

  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setPage(0);
  }, []);

  // ============================================
  // SORTING
  // ============================================

  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    
    const sorted = [...filteredInventoryItems].sort((a, b) => {
      if (property === 'name') {
        return isAsc ? a.name?.localeCompare(b.name) : b.name?.localeCompare(a.name);
      }
      if (property === 'quantity' || property === 'cost_per_unit') {
        const valA = a[property] || 0;
        const valB = b[property] || 0;
        return isAsc ? valA - valB : valB - valA;
      }
      if (property === 'category') {
        return isAsc ? a.category?.localeCompare(b.category) : b.category?.localeCompare(a.category);
      }
      return 0;
    });
    
    setFilteredInventoryItems(sorted);
  }, [orderBy, order, filteredInventoryItems]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData, refreshKey]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderStatsGrid = () => {
    const statItems = [
      { 
        title: 'Total Items', 
        value: stats.total_items || 0, 
        icon: <InventoryIcon fontSize="small" />, 
        color: locationColor 
      },
      { 
        title: 'In Stock', 
        value: Math.max(0, stats.total_items - stats.low_stock_count - stats.out_of_stock_count) || 0, 
        icon: <CheckCircleIcon fontSize="small" />, 
        color: '#4caf50' 
      },
      { 
        title: 'Low Stock', 
        value: stats.low_stock_count || 0, 
        icon: <WarningIcon fontSize="small" />, 
        color: '#ff9800' 
      },
      { 
        title: 'Out of Stock', 
        value: stats.out_of_stock_count || 0, 
        icon: <CancelIcon fontSize="small" />, 
        color: '#f44336' 
      },
      { 
        title: 'Total Quantity', 
        value: stats.total_quantity || 0, 
        icon: <StoreIcon fontSize="small" />, 
        color: '#2196f3' 
      },
      { 
        title: 'Total Value', 
        value: `₦${(stats.total_value || 0).toFixed(2)}`, 
        icon: <MoneyIcon fontSize="small" />, 
        color: '#9c27b0' 
      },
    ];

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={item.title}>
            <StatCard {...item} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCharts = () => {
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Stock Movement Trend */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <ShowChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Stock Movement Trend (Last 14 Days)
            </Typography>
            <Box sx={{ height: 300, width: '100%' }}>
              <ResponsiveContainer>
                <ComposedChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <ChartTooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="purchases" 
                    fill={alpha('#4caf50', 0.3)} 
                    stroke="#4caf50" 
                    name="Purchases"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sales" 
                    fill={alpha('#f44336', 0.3)} 
                    stroke="#f44336" 
                    name="Sales"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="returns" 
                    fill={alpha('#2196f3', 0.3)} 
                    stroke="#2196f3" 
                    name="Returns"
                  />
                  <RechartsBar dataKey="adjustments" fill="#ff9800" name="Adjustments" />
                  <RechartsBar dataKey="wastage" fill="#9e9e9e" name="Wastage" />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Stock Health Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Stock Health
            </Typography>
            <Box sx={{ height: 270, width: '100%' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={stockHealthData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => percent > 0 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
                    labelLine={false}
                  >
                    {stockHealthData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#4caf50', '#ff9800', '#f44336'][index % 3]} />
                    ))}
                  </Pie>
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Category Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Category Distribution
            </Typography>
            <Box sx={{ height: 280, width: '100%' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => percent > 0 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
                    labelLine={false}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Movement Type Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Movement Types
            </Typography>
            <Box sx={{ height: 280, width: '100%' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={movementTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => percent > 0 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
                    labelLine={false}
                  >
                    {movementTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Radar Chart - Stock Performance */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Stock Performance Metrics
            </Typography>
            <Box sx={{ height: 280, width: '100%' }}>
              <ResponsiveContainer>
                <RadarChart data={[
                  { metric: 'In Stock %', value: stats.total_items > 0 ? Math.round(((stats.total_items - stats.low_stock_count - stats.out_of_stock_count) / stats.total_items * 100)) : 0 },
                  { metric: 'Low Stock %', value: stats.total_items > 0 ? Math.round((stats.low_stock_count / stats.total_items * 100)) : 0 },
                  { metric: 'Out of Stock %', value: stats.total_items > 0 ? Math.round((stats.out_of_stock_count / stats.total_items * 100)) : 0 },
                  { metric: 'Active Items %', value: stats.total_items > 0 ? Math.round((stats.active_items / stats.total_items * 100)) : 0 },
                  { metric: 'Avg Value/Item', value: stats.total_items > 0 ? Math.min(100, (stats.total_value / stats.total_items / 1000)) : 0 },
                ]}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar name="Performance" dataKey="value" stroke={locationColor} fill={alpha(locationColor, 0.6)} />
                  <ChartTooltip />
                </RadarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  if (isLoading) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Preloader text={`Loading ${locationName} Inventory Dashboard...`} size={70} fullScreen={true} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <AccountSidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <AccountHeader />
            
            <div className="epos-content">
              {/* Hero Card - View Only */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 800 }}>
                    {locationConfig.icon} {locationName} Inventory Dashboard
                  </div>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Real-time inventory monitoring and analytics (View Only)
                  </Typography>
                </div>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    setSnackbar({
                      open: true,
                      message: '📊 Export functionality coming soon',
                      severity: 'info',
                    });
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  Export Report
                </Button>
              </div>

              {/* Stats Grid */}
              {renderStatsGrid()}

              {/* Charts Section */}
              {renderCharts()}

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  value={tabValue} 
                  onChange={(_, v) => setTabValue(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="📦 Inventory Items" />
                  <Tab label="📈 Stock Movements" />
                  <Tab label="⚠️ Alerts" />
                </Tabs>
              </Box>

              {/* ============================================ */}
              {/* TAB 0: INVENTORY ITEMS - VIEW ONLY */}
              {/* ============================================ */}
              {tabValue === 0 && (
                <Box>
                  {/* Search and Filters */}
                  <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Search inventory by name, category, supplier..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flex: 1, minWidth: 200 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: searchTerm && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setSearchTerm('')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="IN_STOCK">In Stock</MenuItem>
                          <MenuItem value="LOW_STOCK">Low Stock</MenuItem>
                          <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          label="Category"
                        >
                          <MenuItem value="all">All</MenuItem>
                          {Object.keys(stats.categories || {}).map((cat) => (
                            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Clear Filters">
                          <IconButton onClick={handleClearAllFilters} size="small">
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Refresh">
                          <IconButton onClick={() => setRefreshKey(prev => prev + 1)} size="small">
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Inventory Table - View Only with View Stock button */}
                  <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    {loading && <LinearProgress />}
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={() => handleSort('name')}
                              >
                                Item Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'category'}
                                direction={orderBy === 'category' ? order : 'asc'}
                                onClick={() => handleSort('category')}
                              >
                                Category
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'quantity'}
                                direction={orderBy === 'quantity' ? order : 'asc'}
                                onClick={() => handleSort('quantity')}
                              >
                                Quantity
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'cost_per_unit'}
                                direction={orderBy === 'cost_per_unit' ? order : 'asc'}
                                onClick={() => handleSort('cost_per_unit')}
                              >
                                Cost/Unit
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Linked Menu Item</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredInventoryItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                  No Inventory Items Found
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {inventoryItems.length > 0 ? 'Try adjusting your search or filters' : 'No inventory items available for this location'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredInventoryItems
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((item, index) => (
                                <InventoryItemRow
                                  key={item.id}
                                  item={item}
                                  index={page * rowsPerPage + index}
                                  onViewStock={handleViewStock}
                                  onViewDetails={handleViewItem}
                                />
                              ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      component="div"
                      count={filteredInventoryItems.length}
                      page={page}
                      onPageChange={(_, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      sx={{
                        '& .MuiTablePagination-displayedRows': {
                          fontWeight: 500,
                        },
                        '& .MuiTablePagination-selectLabel': {
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Paper>
                </Box>
              )}

              {/* ============================================ */}
              {/* TAB 1: STOCK MOVEMENTS - VIEW ONLY */}
              {/* ============================================ */}
              {tabValue === 1 && (
                <Box>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      All Stock Movements
                    </Typography>
                    {movements.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                          No stock movements recorded
                        </Typography>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Change</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {movements.slice(0, 50).map((movement) => (
                              <MovementRow key={movement.id} movement={movement} />
                            ))}
                          </TableBody>
                        </Table>
                        {movements.length > 50 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2, textAlign: 'center' }}>
                            Showing last 50 movements
                          </Typography>
                        )}
                      </TableContainer>
                    )}
                  </Paper>
                </Box>
              )}

              {/* ============================================ */}
              {/* TAB 2: ALERTS - VIEW ONLY */}
              {/* ============================================ */}
              {tabValue === 2 && (
                <Box>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#ff9800' }} />
                      Inventory Alerts
                    </Typography>
                    {alerts.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50' }} />
                        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                          No Active Alerts
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          All inventory items are at healthy stock levels
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {alerts.map((alert) => (
                          <AlertItem key={alert.id} alert={alert} />
                        ))}
                      </List>
                    )}
                  </Paper>
                </Box>
              )}
            </div>
          </div>
        </div>
        <AccountFooter />
      </div>

      {/* ============================================ */}
      {/* VIEW DETAILS DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h6" fontWeight={700}>
            <ViewIcon sx={{ mr: 1, verticalAlign: 'middle', color: theme.palette.info.main }} />
            Item Details
          </Typography>
          <IconButton onClick={handleCloseViewDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ pt: 3 }}>
          {viewItem && (
            <Box>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: alpha(locationColor, 0.1), color: locationColor }}>
                      <InventoryIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Item Name
                      </Typography>
                      <Typography variant="body1" fontWeight={700} fontSize="1.1rem">
                        {viewItem.name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Category</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewItem.category || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Unit</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewItem.unit}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Quantity</Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {viewItem.quantity}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Status</Typography>
                  <Chip
                    label={STATUS_LABELS[viewItem.status] || viewItem.status}
                    size="small"
                    sx={{
                      backgroundColor: alpha(STATUS_COLORS[viewItem.status] || '#9e9e9e', 0.1),
                      color: STATUS_COLORS[viewItem.status] || '#9e9e9e',
                      fontWeight: 600,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Cost Per Unit</Typography>
                  <Typography variant="body1" fontWeight={500}>₦{viewItem.cost_per_unit?.toFixed(2) || '0.00'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Total Value</Typography>
                  <Typography variant="body1" fontWeight={700} color="#9c27b0">
                    ₦{((viewItem.quantity || 0) * (viewItem.cost_per_unit || 0)).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Min Stock Level</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewItem.minimum_stock_level || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Reorder Point</Typography>
                  <Typography variant="body1" fontWeight={500}>{viewItem.reorder_point || '-'}</Typography>
                </Grid>
                
                {viewItem.supplier && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Supplier</Typography>
                    <Typography variant="body1" fontWeight={500}>{viewItem.supplier}</Typography>
                  </Grid>
                )}
                {viewItem.menu_item_name && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Linked Menu Item</Typography>
                    <Chip 
                      label={viewItem.menu_item_name} 
                      size="small" 
                      icon={<LinkIcon sx={{ fontSize: 14 }} />}
                      sx={{ fontWeight: 500, mt: 0.5 }}
                    />
                  </Grid>
                )}
                {viewItem.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Description</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {viewItem.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* VIEW STOCK DIALOG - EXACT PATTERN FROM EXAMPLE */}
      {/* ============================================ */}
      <Dialog
        open={viewStockDialogOpen}
        onClose={handleCloseViewStockDialog}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <StoreIcon sx={{ mr: 1, color: theme.palette.info.main }} />
              Stock Information: {viewStockItem?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {viewStockItem?.category} • ₦{viewStockItem?.cost_per_unit?.toFixed(2) || '0.00'} per unit
            </Typography>
          </Box>
          <IconButton onClick={handleCloseViewStockDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {loadingStockData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : viewStockData ? (
            <>
              {/* Stock Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#4caf50', 0.04), borderLeft: '4px solid #4caf50' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Current Stock</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {viewStockData.quantity} {viewStockData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#ff9800', 0.04), borderLeft: '4px solid #ff9800' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Reorder Point</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {viewStockData.reorder_point || 0} {viewStockData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#2196f3', 0.04), borderLeft: '4px solid #2196f3' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Chip
                        label={viewStockData.status?.replace('_', ' ') || 'Unknown'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: alpha(
                            viewStockData.status === 'IN_STOCK' ? '#4caf50' :
                            viewStockData.status === 'LOW_STOCK' ? '#ff9800' :
                            viewStockData.status === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e',
                            0.1
                          ),
                          color: viewStockData.status === 'IN_STOCK' ? '#4caf50' :
                                 viewStockData.status === 'LOW_STOCK' ? '#ff9800' :
                                 viewStockData.status === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e',
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#9c27b0', 0.04), borderLeft: '4px solid #9c27b0' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Value</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        ₦{((viewStockData.quantity || 0) * (viewStockData.cost_per_unit || 0)).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Stock Movements History */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                <HistoryIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                Stock Movement History
              </Typography>
              
              {viewStockMovements.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No stock movements recorded for this item
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Previous</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewStockMovements.slice(0, 50).map((movement) => {
                        const isIn = movement.movement_type === 'PURCHASE' || movement.movement_type === 'RETURN';
                        return (
                          <TableRow key={movement.id}>
                            <TableCell>
                              {new Date(movement.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={MOVEMENT_TYPE_LABELS[movement.movement_type] || movement.movement_type}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(
                                    isIn ? '#4caf50' : '#f44336',
                                    0.1
                                  ),
                                  color: isIn ? '#4caf50' : '#f44336',
                                  fontWeight: 600,
                                }}
                                icon={isIn ? <InIcon fontSize="small" /> : <OutIcon fontSize="small" />}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: isIn ? '#4caf50' : '#f44336' }}>
                              {isIn ? '+' : '-'}{movement.quantity} {movement.unit}
                            </TableCell>
                            <TableCell>{movement.previous_quantity}</TableCell>
                            <TableCell>{movement.new_quantity}</TableCell>
                            <TableCell>{movement.reason || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No Inventory Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                No inventory item data available
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseViewStockDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* SNACKBAR */}
      {/* ============================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Suspense>
  );
};

export default InventoryDashboard;